import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Shield, Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('blishcrown_cookie_consent');
    if (!consent) {
      // Small delay before showing banner
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('blishcrown_cookie_consent', JSON.stringify({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    localStorage.setItem('blishcrown_cookie_consent', JSON.stringify({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleCustomize = () => {
    setShowDetails(!showDetails);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
      <Card className="max-w-4xl mx-auto bg-gray-900/95 backdrop-blur-lg border-gray-700 shadow-2xl">
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-yellow-500/20">
                <Cookie className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Cookie Consent</h3>
                <p className="text-sm text-gray-400">We value your privacy</p>
              </div>
            </div>
            <button 
              onClick={handleRejectNonEssential}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Content */}
          <div className="mt-4 text-sm text-gray-300 leading-relaxed">
            <p>
              Blishcrown uses cookies to enhance your gaming experience, analyze site traffic, 
              and personalize content. By clicking "Accept All", you consent to our use of cookies.
            </p>
          </div>

          {/* Detailed Options */}
          {showDetails && (
            <div className="mt-4 space-y-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Essential Cookies</p>
                  <p className="text-xs text-gray-400">Required for site functionality</p>
                </div>
                <span className="text-green-400 text-sm">Always Active</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Analytics Cookies</p>
                  <p className="text-xs text-gray-400">Help us understand how you use our site</p>
                </div>
                <span className="text-gray-400 text-sm">Optional</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Marketing Cookies</p>
                  <p className="text-xs text-gray-400">Used for personalized ads and content</p>
                </div>
                <span className="text-gray-400 text-sm">Optional</span>
              </div>
            </div>
          )}

          {/* Privacy Link */}
          <div className="mt-4 flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-gray-400">Read our</span>
            <Link to="/privacy-policy" className="text-cyan-400 hover:text-cyan-300 underline">
              Privacy Policy
            </Link>
            <span className="text-gray-400">to learn how we protect your data.</span>
          </div>

          {/* Action Buttons */}
          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              onClick={handleAcceptAll}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold"
            >
              Accept All
            </Button>
            <Button
              onClick={handleRejectNonEssential}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Reject Non-Essential
            </Button>
            <Button
              onClick={handleCustomize}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              {showDetails ? 'Hide Details' : 'Customize'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
